import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import "dayjs/locale/he";
import { Skeleton } from "antd";
import { getUserDetails } from "../../../services/authService";
import { getTopAgentsOfMonth } from "../../../services/dashboardTopAgentsApi";
import { getUserInfo } from "../../../services/localStorage";
import {
  clearTopAgentsOfMonthDailyCache,
  msUntilNextLocalMidnight,
  readTopAgentsOfMonthDailyCache,
  resolveTopAgentsCacheAgentKey,
  writeTopAgentsOfMonthDailyCache,
} from "../../../utils/topAgentsOfMonthDailyCache";
import "./TopAgentsOfMonthCard.scss";

dayjs.locale("he");

const RANK_LABELS = {
  1: "🥇",
  2: "🥈",
  3: "🥉",
};

const LEADERS_FETCH_DELAY_MS = 400;

function formatMonthLabel(month) {
  if (!month) return "";
  const parsed = dayjs(`${month}-01`);
  return parsed.isValid() ? parsed.format("MMMM YYYY") : month;
}

function formatPoints(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return new Intl.NumberFormat("he-IL").format(n);
}

function readInitialTopAgentsCache(agentKey, month) {
  try {
    return readTopAgentsOfMonthDailyCache(agentKey, month);
  } catch {
    return null;
  }
}

const TopAgentsOfMonthCard = ({ readyToFetch = true }) => {
  const cacheAgentKey = useMemo(
    () => resolveTopAgentsCacheAgentKey(getUserInfo(), getUserDetails()),
    []
  );
  const [month, setMonth] = useState(() => dayjs().format("YYYY-MM"));
  const initialCache = useMemo(
    () => readInitialTopAgentsCache(cacheAgentKey, month),
    // Sync hydrate once on mount for today's day+month.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const [payload, setPayload] = useState(() => initialCache?.payload ?? null);
  const [loading, setLoading] = useState(() => !initialCache?.payload);
  const [error, setError] = useState(null);
  /** After local midnight, allow one quiet network refresh into the new day's cache. */
  const [forceRefresh, setForceRefresh] = useState(false);

  useEffect(() => {
    if (!readyToFetch) return undefined;

    const cached =
      !forceRefresh
        ? readTopAgentsOfMonthDailyCache(cacheAgentKey, month)
        : null;
    if (cached?.payload) {
      setPayload(cached.payload);
      setLoading(false);
      setError(null);
      return undefined;
    }

    let cancelled = false;
    let delayTimer;
    let retryTimer;

    const load = async (attempt = 0) => {
      // Quiet midnight refresh keeps existing rows visible (no skeleton).
      if (!forceRefresh) setLoading(true);
      setError(null);
      try {
        const data = await getTopAgentsOfMonth({ month, limit: 5 });
        if (cancelled) return;
        setPayload(data);
        writeTopAgentsOfMonthDailyCache(cacheAgentKey, month, data);
        setForceRefresh(false);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        if (attempt < 2) {
          retryTimer = window.setTimeout(() => {
            if (!cancelled) load(attempt + 1);
          }, 600);
          return;
        }
        setError(err);
        if (!forceRefresh) setPayload(null);
        setLoading(false);
      }
    };

    delayTimer = window.setTimeout(() => {
      if (!cancelled) load();
    }, LEADERS_FETCH_DELAY_MS);

    return () => {
      cancelled = true;
      if (delayTimer) window.clearTimeout(delayTimer);
      if (retryTimer) window.clearTimeout(retryTimer);
    };
    // `payload` intentionally omitted — used only to decide skeleton vs quiet refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, readyToFetch, cacheAgentKey, forceRefresh]);

  // Quiet refresh at next local midnight if the leaders tab stays mounted.
  useEffect(() => {
    let cancelled = false;
    let timerId = null;

    const schedule = () => {
      const delay = msUntilNextLocalMidnight();
      timerId = window.setTimeout(() => {
        if (cancelled) return;
        clearTopAgentsOfMonthDailyCache(cacheAgentKey);
        setMonth(dayjs().format("YYYY-MM"));
        setForceRefresh(true);
        schedule();
      }, delay);
    };

    schedule();
    return () => {
      cancelled = true;
      if (timerId != null) window.clearTimeout(timerId);
    };
  }, [cacheAgentKey]);

  const leaders = payload?.leaders ?? [];
  // Prefer client calendar month for the badge (API `month` can lag / be wrong).
  const monthLabel = formatMonthLabel(month || payload?.month);
  const showViewerRank = payload?.viewerRank != null;

  return (
    <div className="top-agents-card" dir="rtl">
      <div className="top-agents-card__header">
        <div className="top-agents-card__header-main">
          <h2 className="top-agents-card__title">🏆 המובילים של החודש</h2>
          <span className="top-agents-card__badge">{monthLabel}</span>
        </div>
      </div>

      <div className="top-agents-card__body">
        {loading ? (
          <Skeleton active paragraph={{ rows: 4 }} />
        ) : error ? (
          <div className="top-agents-card__error">
            <p>לא ניתן לטעון את הדירוג כרגע</p>
            <p className="top-agents-card__error-hint">
              נסה לרענן את הדף. אם הבעיה נמשכת — ודא שה-backend רץ מחדש.
            </p>
          </div>
        ) : leaders.length === 0 ? (
          <p className="top-agents-card__empty">עדיין אין מכירות בחודש זה</p>
        ) : (
          <>
            <table className="top-agents-card__table">
              <thead>
                <tr>
                  <th scope="col">דירוג</th>
                  <th scope="col">סוכן</th>
                  <th scope="col">נקודות</th>
                </tr>
              </thead>
              <tbody>
                {leaders.map((leader) => (
                  <tr
                    key={leader.agentId}
                    className={
                      leader.isCurrentUser
                        ? "top-agents-card__row top-agents-card__row--current"
                        : "top-agents-card__row"
                    }
                  >
                    <td className="top-agents-card__rank">
                      <span className="top-agents-card__rank-value">
                        {RANK_LABELS[leader.rank] || leader.rank}
                      </span>
                    </td>
                    <td className="top-agents-card__name">
                      {leader.displayName}
                      {leader.isCurrentUser ? (
                        <span className="top-agents-card__you-tag">את/ה</span>
                      ) : null}
                    </td>
                    <td className="top-agents-card__points">
                      {formatPoints(leader.points)} נק׳
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {showViewerRank ? (
              <p className="top-agents-card__viewer-rank">
                המיקום שלך: #{payload.viewerRank}
                {payload.viewerPoints != null
                  ? ` · ${formatPoints(payload.viewerPoints)} נק׳`
                  : null}
              </p>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

export default TopAgentsOfMonthCard;
